import type { GetAllEntitiesProgressHandlers } from '../hooks/use-get-all-entities-of-type/use-get-all-entities-of-type';
import type { BulkActionReportingStore } from './bulk-action-reporting.store';
import { withBestEffortReporting } from './with-best-effort-reporting';

/**
 * Creates `onPageFetched` and `onEntityIdsResolved` progress handlers that
 * stream entity IDs into the reporting store as "loading" rows.
 *
 * Callers are expected to have already opened a session via
 * `reportingStore.getState().createSession(...)` before invoking the fetch —
 * this helper only records per-page items and updates the total count once
 * all IDs are resolved (so the progress bar switches from indeterminate to
 * determinate mode). All calls are best-effort and swallow errors so a
 * reporting failure can never break the underlying operation.
 *
 * Used by both `useExportEntities` and `useCopyAllEntitiesToEnvironmentBulkAction`.
 */
export const createPaginatedFetchProgressHandlers = ({
  reportingStore,
  domainEntityType,
}: {
  reportingStore: BulkActionReportingStore;
  domainEntityType: string;
}): Required<
  Pick<GetAllEntitiesProgressHandlers, 'onPageFetched' | 'onEntityIdsResolved'>
> => ({
  onPageFetched: ({ pageEntityIds }) => {
    withBestEffortReporting(() => {
      pageEntityIds.forEach((entityId) => {
        reportingStore.getState().recordItemResult({
          sourceEntityId: entityId,
          entityType: domainEntityType,
          status: 'loading',
        });
      });
    });
  },
  onEntityIdsResolved: ({ totalCount }) => {
    withBestEffortReporting(() => {
      reportingStore.getState().updateTotalItems(totalCount);
    });
  },
});
